import { useEffect, useRef } from 'react';

type AmountValue = number | string | null | undefined;

const isNetFundingEmpty = (value: AmountValue) =>
  value === undefined || value === null || value === '';

/**
 * Keeps loan amount and net funding in step with the dealer fee.
 * Editing net funding recomputes the loan amount, and the other way around.
 * When net funding is still empty, it is filled from the loan amount once.
 */
export const useLinkedLoanAmounts = ({
  loanAmount,
  netFunding,
  dealerFee,
  activeAmountInput,
  loanAmountField,
  netFundingField,
  setLoanAmount,
  setNetFunding,
  echoLoanAmount,
  enabled = true,
  skipInitialSync = false,
}: {
  loanAmount: AmountValue;
  netFunding: AmountValue;
  dealerFee: number;
  activeAmountInput: string;
  loanAmountField: string;
  netFundingField: string;
  setLoanAmount: (value: number) => void;
  setNetFunding: (value: number) => void;
  echoLoanAmount?: (value: number | string) => void;
  enabled?: boolean;
  skipInitialSync?: boolean;
}) => {
  const skippedNetFundingEdit = useRef(!skipInitialSync);
  const skippedLoanAmountEdit = useRef(!skipInitialSync);

  useEffect(() => {
    if (!enabled) return;
    const amount = Number(loanAmount);
    if (!isNetFundingEmpty(netFunding)) return;
    if (loanAmount == null) return;
    if (!Number.isFinite(amount) || amount <= 0) return;
    if (!Number.isFinite(dealerFee) || dealerFee >= 100) return;

    setNetFunding((amount * (100 - dealerFee)) / 100);
    echoLoanAmount?.(loanAmount);
    // Seed once from the loan amount. Same dependency as the new-applicant form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanAmount, enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (!skippedNetFundingEdit.current) {
      skippedNetFundingEdit.current = true;
      return;
    }
    if (activeAmountInput !== netFundingField) return;
    const nextAmount = (Number(netFunding) / (100 - dealerFee)) * 100;
    if (!Number.isFinite(nextAmount)) return;
    if (nextAmount !== Number(loanAmount)) {
      setLoanAmount(nextAmount);
    }
    // activeAmountInput is read inside and omitted here so selecting a field does not sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerFee, netFunding, enabled]);

  useEffect(() => {
    if (!enabled) return;
    if (!skippedLoanAmountEdit.current) {
      skippedLoanAmountEdit.current = true;
      return;
    }
    if (activeAmountInput !== loanAmountField) return;
    const amount = Number(loanAmount);
    const fee = Number(dealerFee);
    if (!Number.isFinite(amount) || !Number.isFinite(fee)) return;
    const nextNetFunding = (amount * (100 - fee)) / 100;
    if (!Number.isFinite(nextNetFunding)) return;
    if (nextNetFunding !== Number(netFunding)) {
      setNetFunding(nextNetFunding);
      echoLoanAmount?.(amount);
    }
    // activeAmountInput is read inside and omitted here so selecting a field does not sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerFee, loanAmount, netFunding, enabled]);
};
